/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import moment from 'moment';
import { ChartItemKeys } from '../../components/fleet/FullTailView/charts/ChartSeriesItems';
import { AnnotationPoint } from '../../components/fleet/FullTailView/charts/ChartWrappers/TimelineChartHelperFunctioners';
import { InstallationType, TerminalDetails } from '../../shared/entities/terminal';
import TerminalHistory, { TerminalHistoryAllResponse } from '../../shared/entities/terminalHistory';
import { STALE_DATA_GAP_SEC } from '../../utils/GlobalUtils';
import { getOtherTerminalOnAircraft } from '../../utils/TerminalUtilities';
import { massageTerminalDetails } from '../terminals/reducer';
import { Action, ActionCreators } from './actions';

const NUM_PROTOCOLS_TRAFFIC_COMP = 9; // 9 protocols + other = 10 categories
export const OTHER_PROTOCOL_NAME = 'other';

export enum RateKeys {
  FL_RATES = 'flRates',
  RL_RATES = 'rlRates'
}
export interface State {
  readonly isRequestingTerminalHistory: boolean;
  readonly isInitialTerminalHistoryRequestFinished: boolean;
  readonly requestTerminalHistoryError: string | null;

  // raw terminal history data from the backend without any massaging
  readonly terminalHistory: { [key: number]: TerminalHistory };
  readonly otherTerminalHistory: { [key: number]: TerminalHistory };

  // Normalized terminal history data that will be used by the charts.
  // This was specifically created for the protocol charts to aggregate
  // the data into 9 + an other category.
  // This field should be used in any case that the frontend needs to massage the
  // terminal history data that effectively changes the raw data.
  readonly normalizedTerminalHistory: { [key: number]: TerminalHistory };
  readonly normalizedOtherTerminalHistory: { [key: number]: TerminalHistory };

  readonly annotations: Partial<AnnotationPoint>[];
  readonly otherDatapathUuid: string | null;
  readonly chartsXAxisMinZoom: number | null;
  readonly chartsXAxisMaxZoom: number | null;
  readonly chartsXAxisMin: number | null;
  readonly chartsXAxisMax: number | null;
  readonly chartsLastUpdated: { [key in ChartItemKeys]: number };
  readonly allProtocolsModalTimestamp?: number; // Must be unix timestamp
  readonly allProtocolsRateType?: RateKeys;
  readonly allProtocolsUseOtherTerminalHistory: boolean;
}

export const initialTerminalHistoryState: State = {
  isRequestingTerminalHistory: false,
  isInitialTerminalHistoryRequestFinished: false,
  requestTerminalHistoryError: null,
  terminalHistory: {},
  otherTerminalHistory: {},
  normalizedTerminalHistory: {},
  normalizedOtherTerminalHistory: {},
  annotations: [],
  otherDatapathUuid: null,
  chartsXAxisMinZoom: null,
  chartsXAxisMaxZoom: null,
  chartsXAxisMin: null,
  chartsXAxisMax: null,
  chartsLastUpdated: {
    timeline: -1,
    speedAndAltitude: -1,
    flDataRate: -1,
    rlDataRate: -1,
    packetLossPercentage: -1,
    pingAvgLatency: -1,
    fLProtocolUsage: -1,
    fLProtocolUsageOtherTerminal: -1,
    rLProtocolUsage: -1,
    rLProtocolUsageOtherTerminal: -1,
    cumDataUsage: -1,
    antennaPointingAngles: -1,
    antennaPointingAnglesOtherTerminal: -1,
    totalDataVolume: -1,
    snr: -1,
    power: -1,
  },

  allProtocolsUseOtherTerminalHistory: false,
};

export default function reducer(state: State = initialTerminalHistoryState, action: Action): State {
  let partialState: Partial<State> | undefined;
  switch (action.type) {
    case ActionCreators.RequestTerminalHistoryOnce.type:
      partialState = {
        requestTerminalHistoryError: null,
        isRequestingTerminalHistory: true,
        terminalHistory: {}, // reset terminal history for each request
        otherTerminalHistory: {},
        otherDatapathUuid: null,
        chartsXAxisMinZoom: null,
        chartsXAxisMaxZoom: null,
        chartsXAxisMin: null,
        chartsXAxisMax: null,
      };
      break;
    case ActionCreators.ClearReceivedTerminalHistoryError.type:
      partialState = {
        requestTerminalHistoryError: null,
        isRequestingTerminalHistory: false,
      };
      break;
    case ActionCreators.ReceivedTerminalHistoryError.type:
      partialState = {
        requestTerminalHistoryError: action.payload,
        isRequestingTerminalHistory: false,
        isInitialTerminalHistoryRequestFinished: true,
        chartsXAxisMinZoom: null,
        chartsXAxisMaxZoom: null,
      };
      break;
    case ActionCreators.ReceivedTerminalHistorySuccess.type: {
      partialState = {
        ...receivedTerminalHistorySuccess(state, action.payload.response as TerminalHistoryAllResponse),
        isInitialTerminalHistoryRequestFinished: true,
        requestTerminalHistoryError: null,
        isRequestingTerminalHistory: false,
      };

      break;
    }
    case ActionCreators.ReceivedTerminalHistoryUpdateSuccess.type: {
      if (action.payload) {
        let currentMetrics = action.payload.useOtherTerminalOnPlatformDataForUpdate
          ? { ...getOtherTerminalOnAircraft(action.payload.terminal) }
          : { ...action.payload.terminal };

        currentMetrics = massageTerminalDetails(currentMetrics);
        let trafficData: Partial<TerminalDetails> = {};
        let trafficCompData: Partial<TerminalDetails> = {};
        let metricsData: Partial<TerminalDetails> = {};
        let pingData: Partial<TerminalDetails> = {};
        let lastTrafficDataPoint: Partial<TerminalDetails> | null = null;

        let chartsLastUpdated = { ...state.chartsLastUpdated };

        if (currentMetrics) {
          let newMinSourceTime = Number.MAX_VALUE;
          let newMaxSourceTime = Number.MIN_VALUE;

          const parseTerminalMetrics = (
            terminalCurrentMetrics: TerminalDetails,
            terminalHistory: { [key: number]: TerminalHistory },
          ): { [key: number]: TerminalHistory } => {
            const firstHistoryPoint = getFirstDataPoint(terminalHistory);
            // traffic data is older than the first terminal history point by a certain amount that makes it stale
            // or if no history data compare against the current time
            const firstSourcetime =
              firstHistoryPoint && firstHistoryPoint.sourceTime ? firstHistoryPoint.sourceTime : moment().utc().unix();

            const isTrafficDataStale =
              terminalCurrentMetrics.trafficSourceTime &&
              firstSourcetime - terminalCurrentMetrics.trafficSourceTime >= STALE_DATA_GAP_SEC;
            const isTrafficCompDataStale =
              terminalCurrentMetrics.trafficCompSourceTime &&
              firstSourcetime - terminalCurrentMetrics.trafficCompSourceTime >= STALE_DATA_GAP_SEC;
            const isPingDataStale =
              terminalCurrentMetrics.pingTimestamp &&
              firstSourcetime - terminalCurrentMetrics.pingTimestamp >= STALE_DATA_GAP_SEC;
            const isDataStale =
              terminalCurrentMetrics.sourceTime &&
              firstSourcetime - terminalCurrentMetrics.sourceTime >= STALE_DATA_GAP_SEC;

            if (terminalCurrentMetrics.trafficCompSourceTime && !isTrafficCompDataStale) {
              trafficCompData = getDataFromCurrentTerminalMetrics(
                terminalCurrentMetrics,
                terminalCurrentMetrics.trafficCompSourceTime,
                'rlRates',
                'flRates',
                'trafficCompGapSec',
              );
              newMinSourceTime = Math.min(newMinSourceTime, terminalCurrentMetrics.trafficCompSourceTime);
              newMaxSourceTime = Math.max(newMaxSourceTime, terminalCurrentMetrics.trafficCompSourceTime);

              // Update chartLastUpdated timestamps for when the data was last updated
              chartsLastUpdated[ChartItemKeys.fLProtocolUsage] = terminalCurrentMetrics.trafficCompSourceTime;
              chartsLastUpdated[ChartItemKeys.fLProtocolUsageOtherTerminal] =
                terminalCurrentMetrics.trafficCompSourceTime;
              chartsLastUpdated[ChartItemKeys.rLProtocolUsage] = terminalCurrentMetrics.trafficCompSourceTime;
              chartsLastUpdated[ChartItemKeys.rLProtocolUsageOtherTerminal] =
                terminalCurrentMetrics.trafficCompSourceTime;
            }
            if (!isTrafficDataStale) {
              // add traffic data
              if (terminalCurrentMetrics.trafficSourceTime) {
                trafficData = getDataFromCurrentTerminalMetrics(
                  terminalCurrentMetrics,
                  terminalCurrentMetrics.trafficSourceTime,
                  'flDataUseBytesOneMin',
                  'rlDataUseBytesOneMin',
                  'flDataRateBps',
                  'rlDataRateBps',
                  'flUsage',
                  'rlUsage',
                );

                newMinSourceTime = Math.min(newMinSourceTime, terminalCurrentMetrics.trafficSourceTime);
                newMaxSourceTime = Math.max(newMaxSourceTime, terminalCurrentMetrics.trafficSourceTime);

                // Update chartLastUpdated timestamps for when the data was last updated
                chartsLastUpdated[ChartItemKeys.flDataRate] = terminalCurrentMetrics.trafficSourceTime;
                chartsLastUpdated[ChartItemKeys.rlDataRate] = terminalCurrentMetrics.trafficSourceTime;
                chartsLastUpdated[ChartItemKeys.cumDataUsage] = terminalCurrentMetrics.trafficSourceTime;
                chartsLastUpdated[ChartItemKeys.totalDataVolume] = terminalCurrentMetrics.trafficSourceTime;
                chartsLastUpdated[ChartItemKeys.snr] = terminalCurrentMetrics.trafficSourceTime;
                chartsLastUpdated[ChartItemKeys.power] = terminalCurrentMetrics.trafficSourceTime;
              }

              lastTrafficDataPoint = getLastDataPointWithTraffic(terminalHistory);

              // Add FL data use to the cumulative total of FL data use
              if (
                lastTrafficDataPoint &&
                lastTrafficDataPoint.sourceTime &&
                lastTrafficDataPoint.flDataUseBytes &&
                trafficData.sourceTime &&
                trafficData.flDataUseBytesOneMin &&
                trafficData.sourceTime > lastTrafficDataPoint.sourceTime
              ) {
                trafficData.flDataUseBytes = lastTrafficDataPoint.flDataUseBytes + trafficData.flDataUseBytesOneMin;
              } else if (lastTrafficDataPoint) {
                trafficData.flDataUseBytes = lastTrafficDataPoint.flDataUseBytes;
              } else {
                trafficData.flDataUseBytes = trafficData.flDataUseBytesOneMin || 0;
              }

              // Add RL data use to the cumulative total of RL data use
              if (
                lastTrafficDataPoint &&
                lastTrafficDataPoint.sourceTime &&
                lastTrafficDataPoint.rlDataUseBytes &&
                trafficData.sourceTime &&
                trafficData.rlDataUseBytesOneMin &&
                trafficData.sourceTime > lastTrafficDataPoint.sourceTime
              ) {
                trafficData.rlDataUseBytes = lastTrafficDataPoint.rlDataUseBytes + trafficData.rlDataUseBytesOneMin;
              } else if (lastTrafficDataPoint) {
                trafficData.rlDataUseBytes = lastTrafficDataPoint.rlDataUseBytes;
              } else {
                trafficData.rlDataUseBytes = trafficData.rlDataUseBytesOneMin || 0;
              }
            }

            if (terminalCurrentMetrics.sourceTime && !isDataStale) {
              metricsData = getDataFromCurrentTerminalMetrics(
                terminalCurrentMetrics,
                terminalCurrentMetrics.sourceTime,
                'velocityKnots',
                'altitudeFeet',
                'azimuthDeg',
                'elevationDeg',
                'skewDeg',
                'polarizationDeg',
                'beamName',
                'beamDisplayName',
                'networkOrigin',
                'flSlaLowerBoundBps',
                'flSlaUpperBoundBps',
                'rlSlaLowerBoundBps',
                'rlSlaUpperBoundBps',
                'serviceRegionName',
                'connectivityStatus',
                'viasatBeamName',
                'gxTotalReceived',
                'gxTotalTransmitted',
                'gxCurrentSnr',
                'gxCalibratedSnr',
                'gxPowerTransmitted',
                'gxPowerReceived',
                'gxPowerFootroom',
                'gxPowerHeadroom',
              );

              newMinSourceTime = Math.min(newMinSourceTime, terminalCurrentMetrics.sourceTime);
              newMaxSourceTime = Math.max(newMaxSourceTime, terminalCurrentMetrics.sourceTime);

              // Update chartLastUpdated timestamps for when the data was last updated
              chartsLastUpdated[ChartItemKeys.speedAndAltitude] = terminalCurrentMetrics.sourceTime;
              chartsLastUpdated[ChartItemKeys.antennaPointingAngles] = terminalCurrentMetrics.sourceTime;
              chartsLastUpdated[ChartItemKeys.antennaPointingAnglesOtherTerminal] = terminalCurrentMetrics.sourceTime;
              chartsLastUpdated[ChartItemKeys.timeline] = terminalCurrentMetrics.sourceTime;
              chartsLastUpdated[ChartItemKeys.totalDataVolume] = terminalCurrentMetrics.sourceTime;
              chartsLastUpdated[ChartItemKeys.snr] = terminalCurrentMetrics.sourceTime;
              chartsLastUpdated[ChartItemKeys.power] = terminalCurrentMetrics.sourceTime;

              // use beam name if beam display name is null
              if (isNil(metricsData.beamDisplayName) && !isNil(metricsData.beamName)) {
                metricsData.beamDisplayName = metricsData.beamName;
              }
            }
            if (terminalCurrentMetrics.pingTimestamp && !isPingDataStale) {
              pingData = getDataFromCurrentTerminalMetrics(
                terminalCurrentMetrics,
                terminalCurrentMetrics.pingTimestamp,
                'packetLossPercentage',
                'pingAvgLatency',
              );

              newMinSourceTime = Math.min(newMinSourceTime, terminalCurrentMetrics.pingTimestamp);
              newMaxSourceTime = Math.max(newMaxSourceTime, terminalCurrentMetrics.pingTimestamp);

              // Update chartLastUpdated timestamps for when the data was last updated
              chartsLastUpdated[ChartItemKeys.packetLossPercentage] = terminalCurrentMetrics.pingTimestamp;
              chartsLastUpdated[ChartItemKeys.pingAvgLatency] = terminalCurrentMetrics.pingTimestamp;
            }

            return merge(
              { ...terminalHistory },
              terminalCurrentMetrics.sourceTime && !isDataStale
                ? {
                    [terminalCurrentMetrics.sourceTime]: {
                      sourceTime: terminalCurrentMetrics.sourceTime,
                      ...metricsData,
                    },
                  }
                : {},
              terminalCurrentMetrics.trafficCompSourceTime && !isTrafficCompDataStale
                ? {
                    [terminalCurrentMetrics.trafficCompSourceTime]: {
                      sourceTime: terminalCurrentMetrics.trafficCompSourceTime,
                      ...trafficCompData,
                    },
                  }
                : {},
              terminalCurrentMetrics.trafficSourceTime && !isTrafficDataStale
                ? {
                    [terminalCurrentMetrics.trafficSourceTime]: {
                      sourceTime: terminalCurrentMetrics.trafficSourceTime,
                      ...trafficData,
                    },
                  }
                : {},
              terminalCurrentMetrics.pingTimestamp && !isPingDataStale
                ? {
                    [terminalCurrentMetrics.pingTimestamp]: {
                      sourceTime: terminalCurrentMetrics.pingTimestamp,
                      ...pingData,
                    },
                  }
                : {},
            );
          };

          const { terminalsOnAircraft } = currentMetrics;

          let termHist = parseTerminalMetrics(currentMetrics, state.terminalHistory);
          const chartsUpdated = { ...chartsLastUpdated };
          let otherTermHist =
            terminalsOnAircraft &&
            terminalsOnAircraft.length &&
            terminalsOnAircraft[0].installationType === InstallationType.KU_PLUS_KA
              ? parseTerminalMetrics(
                  terminalsOnAircraft[0],
                  state.otherTerminalHistory ? state.otherTerminalHistory : {},
                )
              : state.otherTerminalHistory;

          partialState = {
            terminalHistory: termHist,
            normalizedTerminalHistory: normalizeTerminalHistoryData(termHist),
            chartsLastUpdated: chartsUpdated,
            otherTerminalHistory: otherTermHist,
            normalizedOtherTerminalHistory: normalizeTerminalHistoryData(otherTermHist),
          };

          // check for a new min or max for the charts for sourceTime
          if (
            newMinSourceTime !== Number.MAX_VALUE &&
            (!state.chartsXAxisMin || newMinSourceTime < state.chartsXAxisMin)
          ) {
            partialState = {
              ...partialState,
              chartsXAxisMin: newMinSourceTime,
            };
          }

          if (
            newMaxSourceTime !== Number.MIN_VALUE &&
            (!state.chartsXAxisMax || newMaxSourceTime > state.chartsXAxisMax)
          ) {
            partialState = {
              ...partialState,
              chartsXAxisMax: newMaxSourceTime,
            };
          }
        }
      }
      break;
    }
    case ActionCreators.SetChartsXAxisMinMax.type:
      partialState = {
        chartsXAxisMinZoom: action.payload.min,
        chartsXAxisMaxZoom: action.payload.max,
      };
      break;
    case ActionCreators.SetAnnotations.type:
      partialState = {
        annotations: action.payload,
      };
      break;
    case ActionCreators.SetAllProtocolsModalData.type:
      partialState = {
        allProtocolsModalTimestamp: action.payload.allProtocolsModalTimestamp,
        allProtocolsRateType: action.payload.allProtocolsRateType,
        allProtocolsUseOtherTerminalHistory: action.payload.allProtocolsUseOtherTerminalHistory,
      };
      break;
    case ActionCreators.SetAllProtocolsModalTimestamp.type:
      partialState = {
        allProtocolsModalTimestamp: action.payload,
      };
      break;
    case ActionCreators.SetAllProtocolsModalRateKeys.type:
      partialState = {
        allProtocolsRateType: action.payload,
      };
      break;
    case ActionCreators.SetAllProtocolsModalUseOtherTerminalHistory.type:
      partialState = {
        allProtocolsUseOtherTerminalHistory: action.payload,
      };
      break;
    default:
      return state;
  }
  return { ...state, ...partialState };
}

const getLastDataPointWithTraffic = (terminalHistory: { [key: number]: TerminalHistory }): TerminalHistory | null => {
  let lastDataPoint: TerminalHistory | null = null;

  let lastDataPointTimestamp = Object.keys(terminalHistory)
    .sort((a: string, b: string) => parseInt(b) - parseInt(a))
    .find((timestampKey: string): boolean => {
      let timestampKeyInt = parseInt(timestampKey);

      return Boolean(
        terminalHistory[timestampKeyInt] &&
          terminalHistory[timestampKeyInt].flDataUseBytes &&
          terminalHistory[timestampKeyInt].rlDataUseBytes,
      );
    });

  if (lastDataPointTimestamp) {
    lastDataPoint = terminalHistory[parseInt(lastDataPointTimestamp)];
  }

  return lastDataPoint;
};

/**
 * Gets the first point in time for the terminal history i.e. the furthest left point on the timeline
 * @param terminalHistory the terminal history data
 */
const getFirstDataPoint = (terminalHistory: { [key: number]: TerminalHistory }): TerminalHistory | null => {
  let firstDataPoint: TerminalHistory | null = null;

  let firstDataPointTimestamp = Object.keys(terminalHistory).sort(
    (a: string, b: string) => parseInt(a) - parseInt(b),
  )[0];

  if (firstDataPointTimestamp) {
    firstDataPoint = terminalHistory[parseInt(firstDataPointTimestamp)];
  }

  return firstDataPoint;
};

export const getDataFromCurrentTerminalMetrics = (
  currentMetrics: TerminalDetails,
  timestampToAdd: number,
  ...valuesToAdd: (keyof TerminalDetails)[]
): Partial<TerminalDetails> => {
  const newDataToAddInTimestamp = {};

  // get the new data from the current metrics
  if (valuesToAdd) {
    valuesToAdd.forEach((currentMetricsKey: string) => {
      newDataToAddInTimestamp[currentMetricsKey] = currentMetrics[currentMetricsKey];
    });
  }

  return {
    sourceTime: timestampToAdd,
    ...newDataToAddInTimestamp, // add the new data to the timestamp
  };
};

export const normalizeTerminalHistoryData = (tempSortedTerminalHistory: { [key: number]: TerminalHistory }) => {
  const sortedTerminalHistory = Object.values(tempSortedTerminalHistory).sort(
    (a: TerminalHistory, b: TerminalHistory) => a.sourceTime - b.sourceTime,
  );

  // get the protocols to show for traffic data
  const flProtocols = {};
  const rlProtocols = {};

  const addProtocols = (protocols: { [key: string]: boolean }, historyKey: string) => {
    let tHistoryIdx = sortedTerminalHistory.length - 1;
    let protocolCount = 0;

    // start at the most recent timestamp and add protocols until the max number of protocols is reached
    while (protocolCount < NUM_PROTOCOLS_TRAFFIC_COMP && tHistoryIdx >= 0) {
      const termHistoryPoint = sortedTerminalHistory[tHistoryIdx];
      const rates = get(termHistoryPoint, historyKey, null);

      if (rates) {
        const keys = Object.keys(rates);
        keys.sort((a: string, b: string) => rates[b] - rates[a]);

        let keysIdx = 0;
        while (keysIdx < keys.length && protocolCount < NUM_PROTOCOLS_TRAFFIC_COMP) {
          const pKey = keys[keysIdx];

          if (!protocols[pKey]) {
            protocols[pKey] = true;
            protocolCount += 1;
          }
          keysIdx += 1;
        }
      }

      tHistoryIdx -= 1;
    }
  };
  addProtocols(flProtocols, 'flRates');
  addProtocols(rlProtocols, 'rlRates');

  return sortedTerminalHistory.reduce(
    (accumTermHistory: { [key: string]: TerminalHistory }, currValue: TerminalHistory) => {
      const historyPoint = { ...currValue };
      const flRates: { [key: string]: number } | null = get(currValue, 'flRates', null);
      const rlRates: { [key: string]: number } | null = get(currValue, 'rlRates', null);

      const getProtocolObj = (rateObj: { [key: string]: number }, protocols: { [key: string]: boolean }) => {
        let protocolObj = {};
        // add the entries if they correspond with the protocols to show on the chart,
        // otherwise put the entries into the other category
        Object.entries(rateObj).forEach(([key, val]: [string, number]) => {
          if (protocols[key]) {
            protocolObj[key] = val;
          } else {
            protocolObj[OTHER_PROTOCOL_NAME] = protocolObj[OTHER_PROTOCOL_NAME]
              ? protocolObj[OTHER_PROTOCOL_NAME] + val
              : val;
          }
        });

        // add 0 for protocols that are not in the current timestamp
        Object.entries(protocols).forEach(([key]: [string, boolean]) => {
          if (!protocolObj[key]) {
            protocolObj[key] = 0;
          }
        })

        return protocolObj;
      };

      if (flRates) {
        historyPoint.flRates = getProtocolObj(flRates, flProtocols);
      }

      if (rlRates) {
        historyPoint.rlRates = getProtocolObj(rlRates, rlProtocols);
      }

      if (historyPoint.sourceTime) {
        accumTermHistory[historyPoint.sourceTime] = historyPoint;
      }

      return accumTermHistory;
    },
    {},
  );
};

export const receivedTerminalHistorySuccess = (
  state: State = initialTerminalHistoryState,
  allTerminalHistory: TerminalHistoryAllResponse,
) => {
  const { history, traffic, otherTerminalOnAircraft } = allTerminalHistory;
  let xMin: number | null = null;
  let xMax: number | null = null;

  const parseTerminalHistoryData = (
    historyToParse: TerminalHistory[],
    currentTerminalHistory: { [key: number]: TerminalHistory },
  ) => {
    const compiledTerminalHistory = { ...currentTerminalHistory };
    historyToParse
      .map((terminalHistoryPoint: TerminalHistory) => ({
        // take traffic source time in case source time is not present
        // @ts-ignore
        sourceTime: get(terminalHistoryPoint, 'trafficSourceTime', terminalHistoryPoint.sourceTime),
        ...terminalHistoryPoint,
        trafficSourceTime: null,
      }))
      .forEach((terminalHistoryPoint: TerminalHistory) => {
        if (terminalHistoryPoint && terminalHistoryPoint.sourceTime) {
          let dataInTimestamp = {} as Partial<TerminalHistory>;

          // add data from a message with the same timestamp
          if (compiledTerminalHistory[terminalHistoryPoint.sourceTime]) {
            dataInTimestamp = { ...compiledTerminalHistory[terminalHistoryPoint.sourceTime] };
          }

          // use beam name if beam display name is null
          if (dataInTimestamp && isNil(dataInTimestamp.beamDisplayName) && !isNil(dataInTimestamp.beamName)) {
            dataInTimestamp.beamDisplayName = dataInTimestamp.beamName;
          }
          if (isNil(terminalHistoryPoint.beamDisplayName) && !isNil(terminalHistoryPoint.beamName)) {
            terminalHistoryPoint.beamDisplayName = terminalHistoryPoint.beamName;
          }

          compiledTerminalHistory[terminalHistoryPoint.sourceTime] = { ...dataInTimestamp, ...terminalHistoryPoint };
        }
      });
    return compiledTerminalHistory;
  };

  const parseCompiledTerminalHistory = (tempCompiledTerminalHistory: { [key: number]: TerminalHistory }) => {
    // Recalculate the cumulative data to avoid potential cusps in the Cumulative Data Usage chart
    // A cusp could be shown in case the terminal history data has not loaded completely before the new traffic points arrive
    let cumulatedFlDataUse = 0;
    let cumulatedRlDataUse = 0;
    let sortedTerminalHistory = Object.entries(tempCompiledTerminalHistory)
      .map(terminalHistoryPoint => terminalHistoryPoint[1])
      .sort((a: TerminalHistory, b: TerminalHistory) => a.sourceTime - b.sourceTime);

    // check for a stale first point
    if (
      sortedTerminalHistory.length > 2 &&
      sortedTerminalHistory[1].sourceTime - sortedTerminalHistory[0].sourceTime >= STALE_DATA_GAP_SEC
    ) {
      sortedTerminalHistory = sortedTerminalHistory.slice(1);
      xMin = sortedTerminalHistory[0].sourceTime;
    }

    return sortedTerminalHistory.reduce(
      (accumulatedTerminalHistory: { [key: number]: TerminalHistory }, terminalHistoryPoint: TerminalHistory) => {
        const sourceTime = get(terminalHistoryPoint, 'sourceTime', null);

        if (!isNil(sourceTime)) {
          // save the x min and max if found
          if (isNil(xMin) || sourceTime < xMin) {
            xMin = sourceTime;
          }

          if (isNil(xMax) || sourceTime > xMax) {
            xMax = sourceTime;
          }

          let flDataUseOneMin = get(terminalHistoryPoint, 'flDataUseBytesOneMin', null);
          let rlDataUseOneMin = get(terminalHistoryPoint, 'rlDataUseBytesOneMin', null);
          let flDataUse = get(terminalHistoryPoint, 'flDataUseBytes', null);
          let rlDataUse = get(terminalHistoryPoint, 'rlDataUseBytes', null);

          // use the one minute fl and rl data use if present
          // this will be used for the terminal details request points
          if (flDataUseOneMin && rlDataUseOneMin) {
            cumulatedFlDataUse += flDataUseOneMin;
            cumulatedRlDataUse += rlDataUseOneMin;
          } else if (flDataUse && rlDataUse) {
            cumulatedFlDataUse = get(terminalHistoryPoint, 'flDataUseBytes', cumulatedFlDataUse);
            cumulatedRlDataUse = get(terminalHistoryPoint, 'rlDataUseBytes', cumulatedRlDataUse);
          } else {
            // no traffic data for this point
            accumulatedTerminalHistory[sourceTime] = {
              ...terminalHistoryPoint,
            };
            return accumulatedTerminalHistory;
          }

          accumulatedTerminalHistory[sourceTime] = {
            ...terminalHistoryPoint,
            flDataUseBytes: cumulatedFlDataUse,
            rlDataUseBytes: cumulatedRlDataUse,
          };
        }

        return accumulatedTerminalHistory;
      },
      {},
    );
  };

  const parsedTermHistAndTraffic = parseTerminalHistoryData(
    traffic,
    parseTerminalHistoryData(history, state.terminalHistory),
  );
  const sortedAndAccumulatedTerminalHistory = parseCompiledTerminalHistory(parsedTermHistAndTraffic);
  const normalizedTerminalHistory = normalizeTerminalHistoryData(sortedAndAccumulatedTerminalHistory);

  let otherTerminalHistory = {};
  let normalizedOtherTerminalHistory = {};

  // if another terminal history is found
  if (otherTerminalOnAircraft) {
    const parsedOtherTermHistAndTraffic = parseTerminalHistoryData(
      otherTerminalOnAircraft.traffic,
      parseTerminalHistoryData(otherTerminalOnAircraft.history, state.otherTerminalHistory || {}),
    );
    otherTerminalHistory = parseCompiledTerminalHistory(parsedOtherTermHistAndTraffic);
    normalizedOtherTerminalHistory = normalizeTerminalHistoryData(otherTerminalHistory);
  }

  return {
    terminalHistory: sortedAndAccumulatedTerminalHistory,
    otherTerminalHistory,
    normalizedTerminalHistory,
    normalizedOtherTerminalHistory,
    otherDatapathUuid: otherTerminalOnAircraft ? otherTerminalOnAircraft.datapathUuid : null,
    chartsXAxisMinZoom: null,
    chartsXAxisMaxZoom: null,
    chartsXAxisMin: xMin,
    chartsXAxisMax: xMax,
  };
};
